import { START_WHEN, type StartLink, type StartView, type StartWhen } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Check, Compass, FileDown } from 'lucide-react';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { supportUi } from '../../lib/support';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Page, PageHeader, Panel } from '../../ui/layout';

const WHEN_ORDER: StartWhen[] = ['morning', 'day', 'evening', 'regular'];

export const startQuery = { queryKey: ['start'], queryFn: () => api<StartView>('/start') };

/** Ссылка инструкции на страницу портала: путь и отбор вкладки (`/procurement?tab=rfqs`) */
function GuideLink({ link }: { link: StartLink }) {
  const [path, query] = link.path.split('?');
  const search = Object.fromEntries(new URLSearchParams(query ?? ''));
  return (
    <Link
      to={path as '/'}
      search={search as never}
      className="inline-flex min-h-11 items-center rounded-md border border-line px-3 text-[13px] font-medium text-ink hover:border-ink-3 md:min-h-8"
    >
      {link.title}
    </Link>
  );
}

function Links({ links }: { links: StartLink[] }) {
  if (!links.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {links.map((l) => (
        <GuideLink key={l.path} link={l} />
      ))}
    </div>
  );
}

/**
 * «С чего начать» (БЗ-31): та же инструкция, что пришла в письме-приглашении, — по правам человека. Первые шаги
 * с отметками (что портал видит сам — отмечается само), порядок дня, открытые разделы со ссылками, PDF.
 */
export function StartPage() {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery(startQuery);
  const mark = useMutation({
    mutationFn: (v: { key: string; done: boolean }) => api<StartView>('/start/steps', { body: v }),
    onSuccess: (view) => qc.setQueryData(startQuery.queryKey, view),
    onError: () => toast('Отметку сохранить не удалось', 'danger'),
  });
  const demo = Boolean(me.tenant?.isDemo && me.user.demoRole);

  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const v = q.data;
  const percent = v.totalCount ? Math.round((v.doneCount / v.totalCount) * 100) : 0;

  return (
    <Page className="max-w-5xl">
      <PageHeader
        title="С чего начать"
        subtitle={`${v.company} · ${v.roles}`}
        actions={
          <>
            {me.tenant?.staff ? (
              <Button icon={<Compass className="size-4" />} onClick={() => supportUi.openTour()}>
                Экскурсия по порталу
              </Button>
            ) : null}
            <a
              href="/api/v1/start.pdf"
              className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline md:h-10"
            >
              <FileDown className="size-4" aria-hidden /> PDF
            </a>
          </>
        }
      />
      <p className="max-w-3xl text-[15px] leading-6 text-ink">{v.intro}</p>
      <p className="mt-2 max-w-3xl text-[14px] leading-6 text-ink-2">{v.money}</p>

      <div className="mt-6 flex flex-col gap-6">
        <Panel title="Первые шаги" meta={`сделано ${v.doneCount} из ${v.totalCount}`} bodyClassName="p-0">
          <div
            className="h-1.5 bg-tile"
            role="progressbar"
            aria-label="Сделано шагов"
            aria-valuenow={v.doneCount}
            aria-valuemin={0}
            aria-valuemax={v.totalCount}
          >
            <div className="h-full bg-accent transition-[width]" style={{ width: `${percent}%` }} />
          </div>
          <ol>
            {v.firstSteps.map((s, i) => {
              const busy = mark.isPending && mark.variables?.key === s.key;
              return (
                <li key={s.key} className="flex gap-3 border-t border-line px-5 py-4 first:border-0">
                  <button
                    type="button"
                    disabled={s.auto || demo || busy}
                    onClick={() => mark.mutate({ key: s.key, done: !s.done })}
                    aria-pressed={s.done}
                    aria-label={
                      s.auto
                        ? `${s.title}: ${s.done ? 'сделано' : 'отметится само, когда будет сделано'}`
                        : `${s.title}: ${s.done ? 'снять отметку' : 'отметить сделанным'}`
                    }
                    title={
                      s.auto
                        ? 'Портал отметит сам, когда шаг будет сделан'
                        : demo
                          ? 'В демо-режиме отметки не сохраняются'
                          : undefined
                    }
                    className={clsx(
                      'flex size-11 shrink-0 items-center justify-center rounded-md border text-[14px] font-semibold md:size-9',
                      s.done ? 'border-ink bg-ink text-on-ink' : 'border-line-strong bg-surface text-ink-2',
                      !s.auto && !demo && 'hover:border-ink',
                      (s.auto || demo) && 'cursor-default',
                    )}
                  >
                    {s.done ? <Check className="size-4" aria-hidden /> : i + 1}
                  </button>
                  <div className="min-w-0 flex-1">
                    <h3
                      className={clsx(
                        'text-[15px] leading-6 font-semibold',
                        s.done ? 'text-ink-2 line-through decoration-ink-3' : 'text-ink',
                      )}
                    >
                      {s.title}
                    </h3>
                    <p className="mt-0.5 text-[14px] leading-6 text-ink-2">{s.text}</p>
                    {s.auto && !s.done ? (
                      <p className="mt-1 text-[12px] text-ink-3">Отметится само, когда будет сделано</p>
                    ) : null}
                    <Links links={s.links} />
                  </div>
                </li>
              );
            })}
          </ol>
        </Panel>

        <Panel title="Что делать каждый день">
          <div className="flex flex-col gap-6">
            {WHEN_ORDER.map((w) => {
              const items = v.daily.filter((d) => d.when === w);
              if (!items.length) return null;
              return (
                <section key={w}>
                  <h3 className="text-[14px] font-semibold text-ink-2">{START_WHEN[w]}</h3>
                  <ul className="mt-2 flex flex-col gap-4">
                    {items.map((d) => (
                      <li key={d.key}>
                        <p className="text-[15px] leading-6 font-semibold text-ink">{d.title}</p>
                        <p className="text-[14px] leading-6 text-ink-2">{d.text}</p>
                        <Links links={d.links} />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </Panel>

        <Panel title="Ваши разделы" meta={`${v.modules.length}`}>
          <ul className="grid gap-4 md:grid-cols-2">
            {v.modules.map((m) => (
              <li key={m.code} className="rounded-md border border-line p-4">
                <Link
                  to={m.path as '/'}
                  className="inline-flex min-h-11 items-center text-[15px] font-semibold text-ink underline-offset-4 hover:underline md:min-h-0"
                >
                  {m.title}
                </Link>
                <p className="mt-1 text-[14px] leading-6 text-ink-2">{m.summary}</p>
                {m.youCan.length ? (
                  <p className="mt-2 text-[13px] leading-5 text-ink-2">
                    <span className="font-medium text-ink">Вам доступно: </span>
                    {m.youCan.join('; ')}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Если нужна помощь">
          <ul className="flex list-disc flex-col gap-1.5 pl-5 text-[14px] leading-6 text-ink">
            {v.help.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>
        </Panel>
      </div>
    </Page>
  );
}
